// @flow

import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import moment from 'moment';
import { createFragmentContainer, graphql } from 'react-relay';
import * as Actions from 'main-app/store/Actions';
import { i18n, Analytics } from 'shared/utils';
import AddOrderMutation from 'main-app/mutations/AddOrder';
import UpdateOrderMutation from 'main-app/mutations/UpdateOrder';
import AddCustomerMutation from 'main-app/mutations/AddCustomer';
import AddOrderYieldMutation from 'main-app/mutations/AddOrderYield';
import Divider from 'shared/components/common/Divider';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
  SelectCustomerField,
  SelectUserField,
  SelectItemField,
} from 'shared/components/form';
import Button from 'shared/components/common/Button';
import type { AddUpdateOrderModal_order as OrderFragment } from './__generated__/AddUpdateOrderModal_order';
import type { AddUpdateOrderModal_customer as CustomerFragment } from './__generated__/AddUpdateOrderModal_customer';

type Props = {
  onClose: () => void,
  onSuccess?: Object => void,
  onReopen?: () => void,
  order: ?OrderFragment,
  createAnotherOrder?: boolean,
  customer: ?CustomerFragment,
  companyId?: ?string,
};

const AddressPickerWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const AddressChip = styled.button.attrs({ type: 'button' })`
  background: ${p => (p.selected ? '#3b82f6' : '#fff')};
  color: ${p => (p.selected ? '#fff' : '#374151')};
  border: 1px solid ${p => (p.selected ? '#3b82f6' : '#d1d5db')};
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover {
    border-color: #3b82f6;
  }
`;

const DefaultStar = styled.span`
  color: ${p => (p.selected ? '#fff' : '#3b82f6')};
  font-size: 11px;
  font-weight: 700;
`;

const LineItemsTable = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: visible;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const LineItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px 110px 32px;
  gap: 8px;
  padding: 8px 10px;
  align-items: center;
  background: ${p => (p.header ? '#f9fafb' : '#fff')};
  border-bottom: 1px solid #e5e7eb;
  &:last-child { border-bottom: none; }
  position: relative;
  z-index: ${p => p.zIndex || 'auto'};
`;

const LineItemHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const RemoveBtn = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { color: #b91c1c; }
`;

const AddLineBtn = styled.button.attrs({ type: 'button' })`
  background: none;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  color: #3b82f6;
  cursor: pointer;
  font-size: 13px;
  padding: 6px 12px;
  width: 100%;
  text-align: center;
  margin-bottom: 16px;
  &:hover { border-color: #3b82f6; background: #eff6ff; }
`;

const PickerLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const populateAddressFields = (setFieldValue, addr) => {
  setFieldValue('shippingAddressLine1', (addr && addr.addressLine1) || '');
  setFieldValue('shippingAddressLine2', (addr && addr.addressLine2) || '');
  setFieldValue('shippingAddressLine3', (addr && addr.addressLine3) || '');
  setFieldValue('shippingAddressCity', (addr && addr.addressCity) || '');
  setFieldValue('shippingAddressState', (addr && addr.addressState) || '');
  setFieldValue('shippingAddressZip', (addr && addr.addressZip) || '');
};

const clearAddressFields = setFieldValue => {
  setFieldValue('shippingAddressLine1', '');
  setFieldValue('shippingAddressLine2', '');
  setFieldValue('shippingAddressLine3', '');
  setFieldValue('shippingAddressCity', '');
  setFieldValue('shippingAddressState', '');
  setFieldValue('shippingAddressZip', '');
};

const addressMatchesValues = (addr, values) =>
  !!addr &&
  (addr.addressLine1 || '') === (values.shippingAddressLine1 || '') &&
  (addr.addressLine2 || '') === (values.shippingAddressLine2 || '') &&
  (addr.addressLine3 || '') === (values.shippingAddressLine3 || '') &&
  (addr.addressCity || '') === (values.shippingAddressCity || '') &&
  (addr.addressState || '') === (values.shippingAddressState || '') &&
  (addr.addressZip || '') === (values.shippingAddressZip || '');

const LANGUAGE = {
  update: {
    title: i18n.t('Edit Order'),
    submitButton: i18n.t('Update Order'),
    successTitle: i18n.t('Order Successfully Updated'),
    successMessage: userAssigned => {
      if (userAssigned) {
        return i18n.t('The assignee has been notified.');
      }

      return null;
    },
  },
  add: {
    title: i18n.t('Create Order'),
    submitButton: i18n.t('Create Order'),
    successTitle: i18n.t('Order Successfully Created'),
    successMessage: userAssigned => {
      if (userAssigned) {
        return i18n.t(
          'The assignee has been notified. Now create a Job Ticket!',
        );
      }

      return i18n.t('Now create a Job Ticket!');
    },
  },
};

const AddUpdateOrderModal = ({
  onSuccess,
  onClose,
  onReopen,
  order,
  createAnotherOrder,
  customer,
  companyId,
}: Props) => {
  const language = LANGUAGE[order ? 'update' : 'add'];

  return (
    <Modal maxWidth={700}>
      <Formik
        initialValues={{
          lineItems: [],
          customer:
            (order?.customer && {
              value: order?.customer.id,
              data: order?.customer,
            }) ||
            (customer && {
              value: customer.id,
              data: customer,
            }),
          createAnotherOrder,
          customerOrderNumber: order?.customerOrderNumber || '',
          user: order?.user
            ? {
                value: order?.user?.id,
                data: order?.user,
              }
            : // FIXME: this is strictly here for UPBX since user is a required field
            companyId === 'e4e8b3c2-b4f3-47cd-a6ec-38c69c1ae86d'
            ? {
                value: 'ef08a502-d56c-4d0b-b62c-17de2326f864',
              }
            : undefined,
          shippingAddressLine1:
            order?.shippingAddressLine1 ||
            customer?.defaultAddress?.addressLine1 ||
            customer?.shippingAddressLine1 ||
            '',
          shippingAddressLine2:
            order?.shippingAddressLine2 ||
            customer?.defaultAddress?.addressLine2 ||
            customer?.shippingAddressLine2 ||
            '',
          shippingAddressLine3:
            order?.shippingAddressLine3 ||
            customer?.defaultAddress?.addressLine3 ||
            customer?.shippingAddressLine3 ||
            '',
          shippingAddressCity:
            order?.shippingAddressCity ||
            customer?.defaultAddress?.addressCity ||
            customer?.shippingAddressCity ||
            '',
          shippingAddressState:
            order?.shippingAddressState ||
            customer?.defaultAddress?.addressState ||
            customer?.shippingAddressState ||
            '',
          shippingAddressZip:
            order?.shippingAddressZip ||
            customer?.defaultAddress?.addressZip ||
            customer?.shippingAddressZip ||
            '',
          notes: order?.notes || '',
          orderedAt: order?.orderedAt ? moment(order?.orderedAt) : moment(),
          requiredAt: order?.requiredAt ? moment(order?.requiredAt) : null,
          promisedAt: order?.promisedAt ? moment(order?.promisedAt) : null,
        }}
        validationSchema={Yup.object().shape({
          customer: Yup.object()
            .nullable()
            .required('Required'),
          user: Yup.object()
            .nullable()
            .required('Required'),
          createAnotherOrder: Yup.boolean(),
          customerOrderNumber: Yup.string(),
          orderedAt: Yup.date().nullable(),
          requiredAt: Yup.date().nullable(),
          promisedAt: Yup.date().nullable(),
        })}
        onSubmit={async (values: *, { setSubmitting }: *) => {
          setSubmitting(true);

          const mutation = order ? UpdateOrderMutation : AddOrderMutation;

          const variables: any = {
            input: {
              customerId: values.customer?.value || null,
              customerOrderNumber: values.customerOrderNumber || null,
              userId: values.user?.value || null,
              shippingAddressLine1: values.shippingAddressLine1,
              shippingAddressLine2: values.shippingAddressLine2,
              shippingAddressLine3: values.shippingAddressLine3,
              shippingAddressCity: values.shippingAddressCity,
              shippingAddressState: values.shippingAddressState,
              shippingAddressZip: values.shippingAddressZip,
              notes: values.notes,
              orderedAt: values.orderedAt || null,
              requiredAt: values.requiredAt || null,
              promisedAt: values.promisedAt || null,
            },
          };

          if (order) {
            variables.input.id = order.id;
          }

          try {
            if (values.customer && values.customer.__isNew__) {
              const { addCustomer } = await AddCustomerMutation.commit({
                variables: {
                  input: {
                    name: values.customer?.value,
                    shippingAddressLine1: values.shippingAddressLine1,
                    shippingAddressLine2: values.shippingAddressLine2,
                    shippingAddressLine3: values.shippingAddressLine3,
                    shippingAddressCity: values.shippingAddressCity,
                    shippingAddressState: values.shippingAddressState,
                    shippingAddressZip: values.shippingAddressZip,
                  },
                },
              });

              variables.input.customerId = addCustomer.customerEdge.node.id;
            }

            const response = await mutation.commit({
              variables,
            });

            const newOrder = (response.updateOrder || response.addOrder)
              .orderEdge.node;

            // Add line items if creating a new order
            if (!order && values.lineItems && values.lineItems.length > 0) {
              for (const li of values.lineItems) {
                if (li.item && li.item.value && li.quantity > 0) {
                  await AddOrderYieldMutation.commit({
                    variables: {
                      input: {
                        orderId: newOrder.id,
                        itemId: li.item.value,
                        orderedQuantity: parseFloat(li.quantity) || 1,
                        unitPrice: parseFloat(li.unitPrice) || 0,
                      },
                    },
                  });
                }
              }
            }

            if (values.createAnotherOrder && onReopen) {
              onReopen();
            } else {
              onClose();

              if (onSuccess) {
                onSuccess(newOrder);
              }
            }

            Analytics.trackEvent(`${order ? 'Update' : 'Create'} Order`, {
              customer: values.customer?.data?.name,
              dateOrdered: moment(values.orderedAt).format('L'),
              dateRequired: moment(values.requiredAt).format('L'),
              datePromised: moment(values.promisedAt).format('L'),
              assignee:
                values.user?.data?.firstName +
                ' ' +
                values.user?.data?.lastName,
            });
            Actions.alertNotification(
              {
                title: language.successTitle,
                body: language.successMessage(values.user?.value),
              },
              'success',
            );
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'Something Went Wrong');
          }
        }}
        render={({
          values,
          errors,
          isValid,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              <FieldGroupRow
                left={
                  <SelectCustomerField
                    name="customer"
                    creatable
                    clearable
                    error={errors.customer}
                    creatingNewRecord={
                      values.customer && values.customer.__isNew__
                    }
                    onChange={value => {
                      if (!value || !value.value) {
                        clearAddressFields(setFieldValue);
                        return;
                      }

                      const customerData = value.data;

                      if (!customerData) {
                        return;
                      }

                      // Prefer the default address; fall back to the saved
                      // legacy fields on the customer record (used during
                      // migration from single-address customers).
                      if (customerData.defaultAddress) {
                        populateAddressFields(
                          setFieldValue,
                          customerData.defaultAddress,
                        );
                      } else {
                        setFieldValue(
                          'shippingAddressLine1',
                          customerData.shippingAddressLine1 || '',
                        );
                        setFieldValue(
                          'shippingAddressLine2',
                          customerData.shippingAddressLine2 || '',
                        );
                        setFieldValue(
                          'shippingAddressLine3',
                          customerData.shippingAddressLine3 || '',
                        );
                        setFieldValue(
                          'shippingAddressCity',
                          customerData.shippingAddressCity || '',
                        );
                        setFieldValue(
                          'shippingAddressState',
                          customerData.shippingAddressState || '',
                        );
                        setFieldValue(
                          'shippingAddressZip',
                          customerData.shippingAddressZip || '',
                        );
                      }
                    }}
                  />
                }
                right={
                  <FieldGroup
                    name="customerOrderNumber"
                    label={i18n.t('Customer Purchase Order #')}
                    placeholder={i18n.t(`Your Customer's Purchase Order #`)}
                    error={errors.customerOrderNumber}
                    tooltip={i18n.t(
                      `Add your customer's purchase order number for reference when searching through orders.`,
                    )}
                  />
                }
              />
              <SelectUserField
                name="user"
                label={i18n.t('Assignee')}
                error={errors.user}
                tooltip={i18n.t(
                  'The Order Assignee will receive regular updates when changes are made to an order.',
                )}
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="orderedAt"
                    type="date"
                    label={i18n.t('Date Ordered')}
                    placeholder={i18n.t('Date')}
                    error={errors.orderedAt}
                    allowPastDates
                  />
                }
                center={
                  <FieldGroup
                    name="requiredAt"
                    type="date"
                    label={i18n.t('Date Required')}
                    placeholder={i18n.t('Date')}
                    error={errors.requiredAt}
                    minDate={values.orderedAt}
                  />
                }
                right={
                  <FieldGroup
                    name="promisedAt"
                    type="date"
                    label={i18n.t('Date Promised')}
                    placeholder={i18n.t('Date')}
                    error={errors.promisedAt}
                    minDate={values.orderedAt}
                    tooltip="This date is used for auto-scheduling and to let assignees know when jobs are in jeopardy of passing the promise date."
                  />
                }
              />
              <Divider style={{ marginTop: 8, marginBottom: 24 }} />
              {(() => {
                const selectedCustomerData = values.customer?.data;
                const savedAddresses =
                  (selectedCustomerData && selectedCustomerData.addresses) ||
                  [];

                if (savedAddresses.length === 0) {
                  return null;
                }

                return (
                  <div style={{ marginBottom: 16 }}>
                    <PickerLabel>
                      {i18n.t('Use a saved shipping address')}
                    </PickerLabel>
                    <AddressPickerWrap>
                      {savedAddresses.map(addr => {
                        const selected = addressMatchesValues(addr, values);
                        return (
                          <AddressChip
                            key={addr.id}
                            selected={selected}
                            onClick={() =>
                              populateAddressFields(setFieldValue, addr)
                            }
                          >
                            {addr.isDefault && (
                              <DefaultStar selected={selected}>★</DefaultStar>
                            )}
                            {addr.label}
                          </AddressChip>
                        );
                      })}
                      <AddressChip
                        onClick={() => clearAddressFields(setFieldValue)}
                      >
                        {i18n.t('Custom')}
                      </AddressChip>
                    </AddressPickerWrap>
                  </div>
                );
              })()}
              <AddressAutocomplete
                value={values.shippingAddressLine1 || ''}
                label={i18n.t('Shipping Address')}
                placeholder={i18n.t('Shipping Address Line 1')}
                error={errors.shippingAddressLine1}
                onChange={val => setFieldValue('shippingAddressLine1', val)}
                onAddressSelect={({ line1, city, state, zip }) => {
                  if (line1) setFieldValue('shippingAddressLine1', line1);
                  if (city) setFieldValue('shippingAddressCity', city);
                  if (state) setFieldValue('shippingAddressState', state);
                  if (zip) setFieldValue('shippingAddressZip', zip);
                }}
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="shippingAddressLine2"
                    label={i18n.t('Address 2')}
                    placeholder={i18n.t('Shipping Address Line 2')}
                    error={errors.shippingAddressLine2}
                  />
                }
                right={
                  <FieldGroup
                    name="shippingAddressLine3"
                    label={i18n.t('Address 3')}
                    placeholder={i18n.t('Shipping Address Line 3')}
                    error={errors.shippingAddressLine3}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="shippingAddressCity"
                    label={i18n.t('City')}
                    placeholder={i18n.t('City')}
                    error={errors.shippingAddressCity}
                  />
                }
                center={
                  <FieldGroup
                    name="shippingAddressState"
                    label={i18n.t('State')}
                    placeholder={i18n.t('State')}
                    error={errors.shippingAddressState}
                  />
                }
                right={
                  <FieldGroup
                    name="shippingAddressZip"
                    label={i18n.t('Postal Code')}
                    placeholder={i18n.t('Zip Code')}
                    error={errors.shippingAddressZip}
                  />
                }
              />
              <Divider style={{ marginTop: 8, marginBottom: 24 }} />
              {!order && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    {i18n.t('Line Items (optional)')}
                  </div>
                  {values.lineItems.length > 0 && (
                    <LineItemsTable>
                      <LineItemRow header>
                        <LineItemHeader>{i18n.t('Item')}</LineItemHeader>
                        <LineItemHeader>{i18n.t('Qty')}</LineItemHeader>
                        <LineItemHeader>{i18n.t('Unit Price')}</LineItemHeader>
                        <div />
                      </LineItemRow>
                      {values.lineItems.map((li, idx) => (
                        <LineItemRow key={idx} zIndex={values.lineItems.length - idx + 1}>
                          <SelectItemField
                            name={`lineItems[${idx}].item`}
                            label={null}
                            onChange={val => {
                              if (val && val.data) {
                                const sp = val.data.sellingPrice != null
                                  ? val.data.sellingPrice
                                  : val.data.cost != null
                                  ? val.data.cost
                                  : null;
                                if (sp != null) {
                                  setFieldValue(`lineItems[${idx}].unitPrice`, sp);
                                }
                              }
                            }}
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={li.quantity}
                            onChange={e => {
                              const updated = [...values.lineItems];
                              updated[idx] = { ...updated[idx], quantity: e.target.value };
                              setFieldValue('lineItems', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={li.unitPrice}
                            onChange={e => {
                              const updated = [...values.lineItems];
                              updated[idx] = { ...updated[idx], unitPrice: e.target.value };
                              setFieldValue('lineItems', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
                          />
                          <RemoveBtn
                            onClick={() => {
                              const updated = values.lineItems.filter((_, i) => i !== idx);
                              setFieldValue('lineItems', updated);
                            }}
                          >
                            ×
                          </RemoveBtn>
                        </LineItemRow>
                      ))}
                    </LineItemsTable>
                  )}
                  <AddLineBtn
                    onClick={() =>
                      setFieldValue('lineItems', [
                        ...values.lineItems,
                        { item: null, quantity: 1, unitPrice: 0 },
                      ])
                    }
                  >
                    + {i18n.t('Add Line Item')}
                  </AddLineBtn>
                </div>
              )}
              <FieldGroup
                name="notes"
                type="textarea"
                label={i18n.t('Notes')}
                placeholder={i18n.t('Enter notes here...')}
                error={errors.notes}
              />
            </ModalBody>
            <ModalFooter
              style={{
                alignItems: 'center',
              }}
            >
              {!order && (
                <FieldGroup
                  label={i18n.t('Create Another Order')}
                  sideLabel="right"
                  name="createAnotherOrder"
                  error={errors.createAnotherOrder}
                  type="toggle"
                />
              )}
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {language.submitButton}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

AddUpdateOrderModal.defaultProps = {
  onSuccess: undefined,
  onReopen: undefined,
  createAnotherOrder: false,
  companyId: undefined,
};

export default createFragmentContainer(AddUpdateOrderModal, {
  order: graphql`
    fragment AddUpdateOrderModal_order on Order {
      id
      customerOrderNumber
      shippingAddressLine1
      shippingAddressLine2
      shippingAddressLine3
      shippingAddressCity
      shippingAddressState
      shippingAddressZip
      notes
      orderedAt
      requiredAt
      promisedAt
      customer {
        id
        name
      }
      user {
        id
        firstName
        lastName
      }
    }
  `,
  customer: graphql`
    fragment AddUpdateOrderModal_customer on Customer {
      id
      shippingAddressLine1
      shippingAddressLine2
      shippingAddressLine3
      shippingAddressCity
      shippingAddressState
      shippingAddressZip
      addresses {
        id
        label
        addressLine1
        addressLine2
        addressLine3
        addressCity
        addressState
        addressZip
        isDefault
      }
      defaultAddress {
        id
        label
        addressLine1
        addressLine2
        addressLine3
        addressCity
        addressState
        addressZip
      }
    }
  `,
});
